<template>
	<BlockVisibilityObserver title="IN_STOCK">
		<section class="kova-v2-treatment">
			<div class="kova-v2-treatment__inner">
				<h2 class="kova-v2-treatment__title">
					<span
						v-for="(part, index) in titleParts"
						:key="index"
						:class="{ 'kova-v2-treatment__title-accent': part.accent }"
						v-text="part.text"
					/>
				</h2>

				<div class="kova-v2-treatment__cards" :class="{ 'kova-v2-treatment__cards_single': medications.length === 1 }">
					<div class="kova-v2-treatment__meds">
						<article v-for="medication in medications" :key="medication.kind" class="kova-v2-treatment__med">
							<div class="kova-v2-treatment__media">
								<img
									alt=""
									decoding="async"
									height="1024"
									loading="lazy"
									:src="VIAL_SHOTS[medication.kind]"
									width="1024"
								/>
							</div>

							<div class="kova-v2-treatment__copy">
								<h3 class="kova-v2-treatment__name">{{ medication.name }}</h3>
								<!-- Figma wraps "GLP-1/GIP" after the slash; browsers do not break there
								     on their own, so the slash gets an explicit break opportunity. -->
								<p class="kova-v2-treatment__description">
									<template v-for="(segment, i) in slashSegments(medication.description)" :key="i">
										<wbr v-if="i" />
										<span v-text="segment" />
									</template>
								</p>
							</div>

							<p class="kova-v2-treatment__badge" :class="`kova-v2-treatment__badge_${medication.badge.tone}`">
								<InlineSvg
									v-if="medication.badge.icon === 'lightning'"
									aria-hidden="true"
									class="kova-v2-treatment__badge-icon"
									:src="lightning"
								/>
								<InlineSvg v-else aria-hidden="true" class="kova-v2-treatment__badge-icon" :src="clock" />
								<span>{{ medication.badge.label }}</span>
							</p>
						</article>
					</div>

					<!-- The universal desktop frame (6547:16807) has no such card: hidden per breakpoint, one copy. -->
					<article
						class="kova-v2-treatment__included"
						:class="{
							'kova-v2-treatment__included_no-desktop': included.hiddenOnDesktop,
						}"
					>
						<h3 class="kova-v2-treatment__included-title">{{ included.title }}</h3>
						<ul class="kova-v2-treatment__included-list">
							<li v-for="item in included.items" :key="item" class="kova-v2-treatment__included-item">
								<InlineSvg aria-hidden="true" class="kova-v2-treatment__check" :src="check" />
								<span>{{ item }}</span>
							</li>
						</ul>
						<p class="kova-v2-treatment__chip">
							<img
								alt=""
								class="kova-v2-treatment__chip-icon"
								height="16"
								decoding="async"
								loading="lazy"
								:src="delivery"
								width="16"
							/>
							<span>{{ included.shipping }}</span>
						</p>
					</article>
				</div>
			</div>

			<!-- 6547:17172 — desktop only; the mobile frame relies on the floating CTA. -->
			<LandingKovaV2Button
				class="kova-v2-treatment__cta"
				:label="ctaLabel"
				variant="section"
				@click="emit('progressLoaderCompleted', 'in_stock_eligible')"
			/>
		</section>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaV2Button from '@/modules/Landing/components/v2/LandingKovaV2Button.vue'
import { KovaV2CtaEntryPoint, LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import type { MedicationKindEnum } from '@/domain/medication'
import check from '@/assets/icons/ds_check.svg'
import clock from '@/assets/kova/landing/ds-clock-filled.svg'
import delivery from '@/assets/kova/landing/v2/icon-fast-delivery.svg'
import lightning from '@/assets/kova/landing/ds-lightning.svg'
import productSemaglutide from '@/assets/kova/landing/product-vial-semaglutide.png'
import productTirzepatide from '@/assets/kova/landing/product-vial-tirzepatide.png'

defineOptions({ name: 'LandingKovaV2Treatment' })

// The 1024² vial shots of the two cards (6401:45826 / 6401:45844).
const VIAL_SHOTS: Record<MedicationKindEnum, string> = {
	semaglutide: productSemaglutide,
	tirzepatide: productTirzepatide,
}

defineProps<LandingComponentProps.KovaV2Treatment>()

const emit = defineEmits<{ progressLoaderCompleted: [entryPoint: KovaV2CtaEntryPoint] }>()

// "A dual-action GLP-1/GIP …" → ["A dual-action GLP-1/", "GIP …"], rendered with a <wbr>
// between them: the only extra break opportunity, no characters added to the copy.
function slashSegments(text: string): string[] {
	return text.split('/').map((part, i, parts) => (i < parts.length - 1 ? `${part}/` : part))
}
</script>

<style lang="scss" scoped>
// Mobile (6401:45822): 20 top, 32 bottom, 16px gutters, heading 24px above the cards.
.kova-v2-treatment {
	font-family: var(--kova-lp-font);
}

.kova-v2-treatment__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding: var(--size-20) var(--size-16) var(--size-32);
}

.kova-v2-treatment__title {
	margin: 0;

	font-size: var(--size-32);
	font-weight: 500;
	line-height: var(--size-36); // 32px × 1.12 = 35.84px — exact size not in design system
	letter-spacing: -0.64px;
	text-align: center;

	color: var(--kova-lp-ink);
}

.kova-v2-treatment__title-accent {
	color: var(--kova-lp-highlight);
}

.kova-v2-treatment__cards,
.kova-v2-treatment__meds {
	display: flex;
	gap: var(--size-8);
}

.kova-v2-treatment__cards {
	flex-direction: column;
}

// 6401:45826 — two 175px cards side by side on universal, one 358px card otherwise.
.kova-v2-treatment__med {
	box-sizing: border-box;
	display: flex;
	flex: 1 1 0;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--size-12);
	min-width: 0;
	padding: var(--size-14); // 16 in Figma, whose 2px stroke sits inside the box

	border: var(--size-2) solid var(--kova-lp-hairline);
	border-radius: var(--size-24);
	background: var(--Fill-White);
}

// 6401:45834 — 120px grey well, the 180px vial 12px above its top edge.
.kova-v2-treatment__media {
	position: relative;
	overflow: hidden;
	align-self: stretch;
	height: var(--size-120);

	border-radius: var(--size-16);
	background: var(--kova-v2-surface);

	img {
		position: absolute;
		left: calc(50% - 0.5px);
		top: calc(-1 * var(--size-12));

		display: block;
		width: 180px;
		max-width: none;
		height: 180px;

		transform: translateX(-50%);
	}
}

.kova-v2-treatment__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
}

// 6401:45837 — Inter SemiBold 16/22.
.kova-v2-treatment__name {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-22);

	color: var(--Text-Primary-100);
}

.kova-v2-treatment__description {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	color: var(--kova-v2-text-secondary);
}

// 6401:45839 — 6/8 padding, 6 radius, 16px icon, Inter SemiBold 12/16.
.kova-v2-treatment__badge {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;
	padding: var(--size-4) var(--size-8) var(--size-4) var(--size-6);

	font-family: var(--font-family);
	font-size: var(--size-12);
	font-weight: 600;
	line-height: var(--size-16);
	white-space: nowrap;

	border-radius: var(--size-6);
}

// Figma bottom-aligns the card content; the badge carries the slack instead, so the
// picture wells stay level when one name wraps to more lines than its neighbour.
.kova-v2-treatment__med > .kova-v2-treatment__badge {
	margin-top: auto;
}

.kova-v2-treatment__badge_mint {
	color: var(--kova-lp-brand);
	background: var(--kova-lp-tint);

	.kova-v2-treatment__badge-icon {
		color: var(--kova-lp-check); // #2E9A50 — 6401:45840
	}
}

.kova-v2-treatment__badge_purple {
	color: var(--kova-v2-purple);
	background: var(--kova-v2-pink);
}

.kova-v2-treatment__badge-icon {
	flex-shrink: 0;
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

// 6401:45861 — "+ Everything you need", 16/20 padding, 16px between blocks.
.kova-v2-treatment__included {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--size-16);
	padding: var(--size-18) var(--size-14); // 20/16 minus the inside 2px stroke

	border: var(--size-2) solid var(--kova-lp-hairline);
	border-radius: var(--size-24);
	background: var(--Fill-White);
}

.kova-v2-treatment__included-title {
	margin: 0;

	font-family: var(--font-family);
	font-size: var(--size-18);
	font-weight: 600;
	line-height: var(--size-22);

	color: var(--Text-Primary-100);
}

.kova-v2-treatment__included-list {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-v2-treatment__included-item {
	display: flex;
	align-items: flex-start;
	gap: var(--size-8);

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	color: var(--Text-Primary-100);
}

// 6401:45867 — the DS check (`ds_check.svg`) in brand/shot/shot-accent.
.kova-v2-treatment__check {
	flex-shrink: 0;
	display: block;
	width: var(--size-20);
	height: var(--size-20);

	color: var(--kova-lp-check);
}

// 6547:15733 — outlined "Free shipping" chip.
.kova-v2-treatment__chip {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;
	padding: calc(var(--size-4) - 1px) calc(var(--size-12) - 1px); // 4/12 minus the inside 1px stroke

	font-family: var(--font-family);
	font-size: var(--size-14);
	line-height: var(--size-20);

	border: 1px solid var(--Stroke-Secondary);
	border-radius: var(--size-8);
	color: var(--kova-lp-ink);
}

.kova-v2-treatment__chip-icon {
	display: block;
	width: var(--size-16);
	height: var(--size-16);
}

.kova-v2-treatment__cta {
	display: none;
}

// Desktop (6547:17125): 60px block padding, 1200 content, heading 40px above the cards,
// the 397x90 CTA 48px below them.
@media (min-width: 1024px) {
	.kova-v2-treatment {
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--size-48);
		max-width: 1440px;
		margin-inline: auto;
		padding: var(--size-60) var(--size-40);
	}

	.kova-v2-treatment__inner {
		gap: var(--size-40);
		width: 100%;
		max-width: 1200px;
		padding: 0;
	}

	.kova-v2-treatment__title {
		font-size: var(--size-56);
		line-height: var(--size-60); // 56px × 1.1 = 61.60px — exact size not in design system
		letter-spacing: -1.12px;
	}

	.kova-v2-treatment__cards {
		flex-direction: row;
		justify-content: center;
		align-items: stretch;
		gap: var(--size-16);
	}

	.kova-v2-treatment__meds {
		flex: 1 1 0;
		gap: var(--size-16);
		min-width: 0;
	}

	// Single-drug desktop (6566:33522): a 592 card and the 420 benefits card, centred.
	.kova-v2-treatment__cards_single .kova-v2-treatment__meds {
		flex: 0 1 592px;
	}

	// 6547:32060 — 1px hairline, 36 radius; badge and copy above the picture.
	.kova-v2-treatment__med {
		gap: 0;
		padding: calc(var(--size-16) - 1px); // 16 minus the inside 1px stroke

		border-width: 1px;
		border-color: var(--Stroke-Secondary);
		border-radius: var(--size-36);
	}

	.kova-v2-treatment__badge,
	.kova-v2-treatment__med > .kova-v2-treatment__badge {
		order: 0;
		margin: var(--size-16) var(--size-16) 0;

		font-size: var(--size-14);
		line-height: var(--size-20);
	}

	.kova-v2-treatment__copy {
		order: 1;
		gap: var(--size-8);
		margin: var(--size-16) var(--size-16) var(--size-28); // 16 padding + 12 gap above the picture
	}

	.kova-v2-treatment__name {
		font-family: var(--kova-lp-font);
		font-size: var(--size-28);
		font-weight: 500;
		line-height: var(--size-34); // 28px × 1.2 = 33.60px — exact size not in design system
		letter-spacing: -0.56px;

		color: var(--kova-lp-ink);
	}

	.kova-v2-treatment__description {
		font-size: var(--size-16);
		line-height: var(--size-22);

		color: var(--Text-Primary-100);
	}

	// 6547:32072 — 300px well, the 448px vial 36px above its top edge. The tirzepatide
	// frame (6566:34908) draws it 272 tall; one height for all three.
	.kova-v2-treatment__media {
		order: 2;
		height: 300px; // exact size not in design system

		border-radius: var(--size-24);

		img {
			left: 50%;
			top: -36px;

			width: 448px;
			height: 448px;
		}
	}

	// 6566:33522 — 420 wide, 32/32/40 padding, blocks spread over the card height.
	.kova-v2-treatment__included {
		flex: 0 1 420px;
		justify-content: space-between;
		gap: var(--size-16);
		padding: calc(var(--size-32) - 1px) calc(var(--size-32) - 1px) calc(var(--size-40) - 1px); // 32/32/40 minus the inside 1px stroke

		border-width: 1px;
		border-color: var(--Stroke-Secondary);
		border-radius: var(--size-36);
	}

	.kova-v2-treatment__included_no-desktop {
		display: none;
	}

	.kova-v2-treatment__included-title {
		font-family: var(--kova-lp-font);
		font-size: var(--size-28);
		font-weight: 500;
		line-height: var(--size-34); // 28px × 1.2 = 33.60px — exact size not in design system
		letter-spacing: -0.56px;

		color: var(--kova-lp-ink);
	}

	.kova-v2-treatment__included-list {
		gap: var(--size-12);
	}

	.kova-v2-treatment__included-item {
		font-size: var(--size-18);
		line-height: var(--size-24);
		letter-spacing: -0.2px;
	}

	.kova-v2-treatment__check {
		width: var(--size-24);
		height: var(--size-24);
	}

	.kova-v2-treatment__chip {
		gap: var(--size-8);
		padding: calc(var(--size-6) - 1px) calc(var(--size-12) - 1px); // 6/12 minus the inside 1px stroke

		font-size: var(--size-18);
		line-height: var(--size-24);
		letter-spacing: -0.2px;

		border-radius: var(--size-12);
		color: var(--Text-Primary-100);
	}

	.kova-v2-treatment__chip-icon {
		width: var(--size-24);
		height: var(--size-24);
	}

	.kova-v2-treatment__cta {
		display: inline-flex;
	}
}

@media (min-width: 1440px) {
	.kova-v2-treatment {
		padding-inline: var(--size-120);
	}
}
</style>
